import {
  get as apiGet,
  post as apiPost,
  TYPE_BROOKLYN,
  TYPE_BROOKLYN_JSON,
} from 'api-client'
import _get from 'lodash/get'

export const TEXTBOX_REMOTE_LOGIN_CODE = 'remote_login_code'
export const REMOTE_LOGIN_CODE_SUCCESSFULLY_ACTIVATED = 'remoteLoginCodeSuccessfullyActivated'
export const APPROVE_REMOTE_LOGIN_CODE_PROCESSING = 'approveRemoteLoginCodeProcessing'
export const APPROVE_REMOTE_LOGIN_CODE_ERROR_CODE = 'approveRemoteLoginCodeErrorCode'
export const REMOTE_LOGIN_CODE_SUCCESSFULLY_ACTIVATED_CODE = 'activatedCode'

export function getRemoteLoginCodeValidation () {
  return {
    minLength: REMOTE_LOGIN_CODE_LENGTH,
    matchRegexp: REMOTE_LOGIN_CODE_REGEX,
  }
}

const REMOTE_LOGIN_CODE_REGEX = /^\d+$/
const REMOTE_LOGIN_CODE_LENGTH = 6

/**
 * Approves a remote login code.
 *
 * @async
 * @function approveRemoteLoginCode
 * @param {Object} options - The options object.
 * @param {Map} options.auth - The authentication object.
 * @param {string} options.remoteLoginCode - The remote login code to be approved.
 * @returns {Promise<Object>} A promise that resolves with the status or rejects with an error.
 */
export async function approveRemoteLoginCode ({ auth, remoteLoginCode }) {
  try {
    const url = `v1/approve-code/${remoteLoginCode}`
    const res = await apiGet(url, null, { auth }, TYPE_BROOKLYN)
    return handleResponse(res, remoteLoginCode)
  } catch (e) {
    return handleResponse({}, remoteLoginCode, true, e.code)
  }
}

function handleResponse (res, remoteLoginCode, _dataError) {
  const returnVal = {}
  const genericErrorCode = 500
  if (_dataError) {
    returnVal[REMOTE_LOGIN_CODE_SUCCESSFULLY_ACTIVATED] = false
    returnVal[APPROVE_REMOTE_LOGIN_CODE_ERROR_CODE] = genericErrorCode
    return returnVal
  }
  const status = _get(res, 'status')
  if (status === 200) {
    const returnedCode = _get(res.body.data, REMOTE_LOGIN_CODE_SUCCESSFULLY_ACTIVATED_CODE, null)
    returnVal[REMOTE_LOGIN_CODE_SUCCESSFULLY_ACTIVATED] = true
    returnVal[REMOTE_LOGIN_CODE_SUCCESSFULLY_ACTIVATED_CODE] = returnedCode
    return returnVal
  }
  if (status === 400) {
    returnVal[REMOTE_LOGIN_CODE_SUCCESSFULLY_ACTIVATED] = false
    returnVal[APPROVE_REMOTE_LOGIN_CODE_ERROR_CODE] = 400
    return returnVal
  }
  returnVal[REMOTE_LOGIN_CODE_SUCCESSFULLY_ACTIVATED] = false
  returnVal[APPROVE_REMOTE_LOGIN_CODE_ERROR_CODE] = genericErrorCode
  return returnVal
}

/**
* Consumes a remote login code and  and returns data for auth renew
*
* @async
* @function consumeRemoteLoginCode
* @param {Object} options - The options object containing the remote login code.
* @param {string} options.remoteLoginCode - The remote login code to be consumed.
* @returns {Promise<Object>} A promise that resolves with the jwt or rejects with an error.
*/
export async function consumeRemoteLoginCode (options = {}) {
  try {
    const { remoteLoginCode } = options
    const url = `v1/consume-code/${remoteLoginCode}`
    const res = await apiGet(url, null, null, TYPE_BROOKLYN)
    return handleConsumeRemoteLoginResponse(res)
  } catch (e) {
    return handleConsumeRemoteLoginResponse({})
  }
}

export function handleConsumeRemoteLoginResponse (res) {
  const status = _get(res, 'status')
  const jwt = _get(res, 'body.jwt')
  if (status !== 200 || !jwt) {
    throw new Error(_get(res, 'body.errors[0].detail', 'Cannot get remote auth data'))
  }
  return { jwt }
}

/**
 * Request a magic link to log in.
 *
 * @async
 * @function requestMagicLink
 * @param {Object} [params={}] - The options object.
 * @param {string} params.email - The user's email address.
 * @param {string} params.language - The user's language.
 * @returns {Promise<Object>} - A promise that resolves with the remoteLoginCode from the API
 */
export async function requestMagicLink ({ email, language }) {
  try {
    const url = `v1/request-magic-link?language[]=${language}`
    const res = await apiPost(url, { email }, null, TYPE_BROOKLYN_JSON)
    return handleMagicLinkResponse(res)
  } catch (e) {
    return handleMagicLinkResponse({})
  }
}

export function handleMagicLinkResponse (res) {
  const status = _get(res, 'status')
  const remoteLoginCode = _get(res, 'body.remoteLoginCode')
  const expiresAt = _get(res, 'body.expiresAt')
  if (status !== 200 || !remoteLoginCode) {
    throw new Error(_get(res, 'body.errors[0].detail', 'Cannot get remote auth data'))
  }
  return { remoteLoginCode, expiresAt }
}

/**
 * Approves a private login code.
 *
 * @async
 * @function approvePrivateLoginCode
 * @param {Object} options - The options object.
 * @param {string} options.privateCode - The private code to be approved.
 * @returns {Promise<Object>} - A promise that resolves with the response from the API.
 */
export async function approvePrivateLoginCode ({ privateCode }) {
  try {
    const url = `/v1/approve-private-code/${privateCode}`
    const res = await apiGet(url, null, null, TYPE_BROOKLYN)
    return handlePrivateLoginCodeResponse(res)
  } catch (e) {
    return handlePrivateLoginCodeResponse({})
  }
}

export function handlePrivateLoginCodeResponse (res) {
  const status = _get(res, 'status')
  const remoteLoginCode = _get(res, 'body.remoteLoginCode')
  if (status !== 200) {
    throw new Error(_get(res, 'body.errors[0].detail', 'Cannot get remote auth data'))
  }
  return { remoteLoginCode }
}
